import { Injectable, Signal, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import {
  ConsentModalLayout,
  ConsentModalPosition,
  CookieConsentConfig,
  PreferencesModalLayout,
  UserPreferences,
  getUserPreferences,
  run,
} from 'vanilla-cookieconsent';

import { AppInsightsService } from '@mp/shared/app-insights/data-access';
import { AppConfigService } from '@mp/shared/data-access';

@Injectable({
  providedIn: 'root',
})
export class CookieConsentService {
  private readonly appInsightsService = inject(AppInsightsService);
  private readonly appConfigService = inject(AppConfigService);

  private isInitialized = false;

  private readonly APP_INSIGHTS_CONSENT_KEY = 'APP_INSIGHTS_CONSENT';

  private privacyPolicyUrl: Signal<string | undefined> = toSignal(
    this.appConfigService.envConfig$.pipe(map(({ privacyPolicyUrl }) => privacyPolicyUrl)),
  );

  private config: CookieConsentConfig = {
    categories: {
      necessary: {
        enabled: true,
        readOnly: true,
      },
      analytics: {
        autoClear: {
          cookies: [
            { name: '_clck' },
            { name: /^ai_/ },
            { name: /^cfz_/ },
            { name: /^_ga/ },
            { name: /^facebook-pixel_/ },
          ],
        },

        services: {
          microsoftClarity: {
            label: 'Microsoft Clarity',
            cookies: [{ name: '_clck' }],
          },
          applicationInsights: {
            label: 'Application Insights',
            cookies: [{ name: /^ai_/ }],
          },
          cloudflareAnalytics: {
            label: 'Cloudflare Analytics',
            cookies: [{ name: /^cfz_/ }],
          },
          googleAnalytics: {
            label: 'Google Analytics',
            cookies: [{ name: /^_ga/ }],
          },
          facebookPixel: {
            label: 'Facebook Pixel',
            cookies: [{ name: /^facebook-pixel_/ }],
          },
        },
      },
    },
    guiOptions: {
      consentModal: {
        layout: 'box' as ConsentModalLayout,
        position: 'top right' as ConsentModalPosition,
        equalWeightButtons: true,
        flipButtons: false,
      },
      preferencesModal: {
        layout: 'box' as PreferencesModalLayout,
        equalWeightButtons: true,
        flipButtons: false,
      },
    },
    language: {
      default: 'de',
      translations: {
        de: {
          consentModal: {
            title: 'Wir verwenden Cookies',
            description:
              'Wir verwenden Cookies, um ein optimales Nutzererlebnis auf unserer Plattform zu bieten. Einige Cookies sind für den Betrieb der Plattform notwendig, während andere uns helfen, die Nutzung der Plattform zu analysieren und unsere Services zu verbessern. Du kannst selbst entscheiden, welche Cookies du zulassen möchtest.',
            acceptAllBtn: 'Alle akzeptieren',
            acceptNecessaryBtn: 'Nur erforderliche akzeptieren',
            showPreferencesBtn: 'Cookie-Einstellungen verwalten',
          },
          preferencesModal: {
            title: 'Cookie-Einstellungen verwalten',
            acceptAllBtn: 'Alle akzeptieren',
            acceptNecessaryBtn: 'Nur erforderliche akzeptieren',
            savePreferencesBtn: 'Aktuelle Auswahl akzeptieren',
            closeIconLabel: 'Modal schließen',
            sections: [
              {
                title: 'Deine Cookie-Einstellungen',
                description:
                  'In diesem Bereich kannst du einige Präferenzen im Zusammenhang mit der Verarbeitung deiner personenbezogenen Daten äußern. Um deine Zustimmung zu den spezifischen Verarbeitungsaktivitäten, die unten beschrieben sind, zu verweigern, schalte die Schalter auf aus und klicke auf "Aktuelle Auswahl akzeptieren" oder verwende die Schaltfläche „Nur erforderliche akzeptieren“.',
              },
              {
                title: 'Technisch notwendig',
                description:
                  'Diese Cookies sind für die ordnungsgemäße Funktion der Plattform unerlässlich und können nicht deaktiviert werden.',
                linkedCategory: 'necessary',
                cookieTable: {
                  caption: 'Cookie Übersicht',
                  headers: {
                    name: 'Cookie',
                    origin: 'Herkunft',
                    purpose: 'Zweck',
                  },
                  body: [
                    {
                      name: '.AspNetCore.Antiforgery.*',
                      origin: 'ASP.NET Core',
                      purpose: 'Schutz gegen CSRF-Angriffe',
                    },
                    {
                      name: '.AspNetCore.Identity.Application',
                      origin: 'ASP.NET Core',
                      purpose: 'Authentifizierungs-Session für IdentityServer',
                    },
                    {
                      name: '.AspNet.Consent',
                      origin: 'ASP.NET Core',
                      purpose: 'Speichert die Zustimmung zur Cookie-Nutzung',
                    },
                    {
                      name: '.Mediio.Authority',
                      origin: 'Identity Server der Plattform',
                      purpose: 'Session- oder Authentifizierungszwecke',
                    },
                    {
                      name: 'x-ms-routing-name',
                      origin: 'Microsoft Azure',
                      purpose: 'Steuert internes Routing in Azure App Services',
                    },
                    {
                      name: 'TiPMix',
                      origin: 'Azure Traffic Manager',
                      purpose: 'Load Balancing und Routing innerhalb von Azure',
                    },
                    {
                      name: 'HangfireDashboardOrganisation',
                      origin: 'Hangfire Dashboard',
                      purpose: 'Authentifizierung für Hangfire Dashboard',
                    },
                    {
                      name: 'MedioPlattformHangfireDashboard',
                      origin: 'Plattform',
                      purpose: 'Nutzer-Sitzung für das Hangfire Dashboard',
                    },
                    {
                      name: 'cc_cookie',
                      origin: 'CookieConsent',
                      purpose: 'Speichert die Cookie-Einstellungen des Nutzers',
                    },
                  ],
                },
              },
              {
                title: 'Analyse und Tracking',
                description: 'Diese Cookies werden für Nutzerverfolgung oder Performance-Analyse verwendet.',
                linkedCategory: 'analytics',
                cookieTable: {
                  caption: 'Cookie Übersicht',
                  headers: {
                    name: 'Cookie',
                    origin: 'Herkunft',
                    purpose: 'Zweck',
                  },
                  body: [
                    {
                      name: '_clck',
                      origin: 'Microsoft Clarity (azure.com)',
                      purpose: 'Nutzer-Tracking für Microsoft Clarity',
                    },
                    {
                      name: 'ai_session',
                      origin: 'Microsoft Application Insights',
                      purpose: 'Anonyme Session-ID für Performance Tracking',
                    },
                    {
                      name: 'ai_user',
                      origin: 'Microsoft Application Insights',
                      purpose: 'Anonyme Nutzer-ID für Analytics',
                    },
                    {
                      name: 'cfz_google-analytics_v4',
                      origin: 'Cloudflare Analytics',
                      purpose: 'Performance-Tracking über Cloudflare',
                    },
                    {
                      name: 'cfz_reddit',
                      origin: 'Cloudflare / Reddit Pixel',
                      purpose: 'Werbung / Tracking',
                    },
                    {
                      name: '_ga, _ga_*',
                      origin: 'Google Analytics (via Cloudflare)',
                      purpose: 'Nutzerverhalten verfolgen',
                    },
                    {
                      name: 'facebook-pixel_*',
                      origin: 'Facebook Pixel (via Cloudflare)',
                      purpose: 'Werbe-Tracking für Facebook Ads',
                    },
                  ],
                },
              },
              {
                title: 'Weitere Informationen',
                description: `Weitere Informationen zu unseren Cookie-Richtlinien findest du in der <a target="_blank" href="${this.privacyPolicyUrl()}">Datenschutzerklärung</a>.`,
              },
            ],
          },
        },
      },
    },
    // Add callbacks to persist decision and start/stop AppInsights
    onFirstConsent: (cookie) => {
      const allowed: boolean = cookie?.cookie?.services?.['analytics']?.includes('applicationInsights');
      this.setAppInsightsConsent(allowed);

      if (allowed) {
        this.appInsightsService.initializeAppInsights();
      } else {
        this.appInsightsService.destroyAppInsights();
      }
    },
    onConsent: (cookie) => {
      const allowed: boolean = cookie?.cookie?.services?.['analytics']?.includes('applicationInsights');
      this.setAppInsightsConsent(allowed);

      if (allowed) {
        this.appInsightsService.initializeAppInsights();
      }
    },
    onChange: (cookie) => {
      const allowed: boolean = cookie?.cookie?.services?.['analytics']?.includes('applicationInsights');
      this.setAppInsightsConsent(allowed);

      if (allowed) {
        this.appInsightsService.initializeAppInsights();
      } else {
        this.appInsightsService.destroyAppInsights();
      }
    },
  };

  init(): void {
    if (this.isInitialized) {
      return;
    }

    try {
      run(this.config);
      this.checkForAppInsightsConsent();
      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize CookieConsent:', error);
    }
  }

  private setAppInsightsConsent(accepted: boolean): void {
    localStorage.setItem(this.APP_INSIGHTS_CONSENT_KEY, accepted ? '1' : '0');
  }

  private getAppInsightsConsent(): boolean | undefined {
    const v: string | null = localStorage.getItem(this.APP_INSIGHTS_CONSENT_KEY);
    if (v === '1') return true;
    if (v === '0') return false;
    return undefined;
  }

  private checkForAppInsightsConsent(): void {
    // first check persisted decision
    const persisted: boolean | undefined = this.getAppInsightsConsent();
    if (persisted === true) {
      this.appInsightsService.initializeAppInsights();
      return;
    }
    if (persisted === false) {
      this.appInsightsService.destroyAppInsights();
      return;
    }

    // fallback: read cookieconsent preferences if available
    const preferences: UserPreferences = getUserPreferences();
    if (preferences?.rejectedServices?.['analytics']?.includes('applicationInsights')) {
      this.appInsightsService.destroyAppInsights();
      this.setAppInsightsConsent(false);
      return;
    }

    // if explicitly accepted by cookieconsent, init
    this.setAppInsightsConsent(true);
  }
}
